import React from 'react';
import { Shield, Smartphone, Cloud, Wifi, ArrowRight, Check } from 'lucide-react';

const VPN360_URL = 'https://www.vpn360.app';

const services = [
  { icon: Shield, title: 'VPN Móvil', desc: 'Navegación segura y privada' },
  { icon: Wifi, title: 'eSIM Internacional', desc: 'Datos en 190+ países' },
  { icon: Cloud, title: 'Cloud Storage', desc: 'Almacenamiento cifrado' },
];

/**
 * <VPN360Ad variant="strip"|"large" />
 * - strip: compacta para Footer (banda horizontal, aparece en todas las páginas)
 * - large: banner grande para landing (con features)
 */
const VPN360Ad = ({ variant = 'strip' }) => {
  if (variant === 'strip') {
    return (
      <a
        href={VPN360_URL}
        target="_blank"
        rel="noopener noreferrer sponsored"
        className="block bg-gradient-to-r from-indigo-600 via-purple-600 to-blue-600 hover:brightness-110 transition-all"
        data-testid="vpn360-strip-ad"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-3 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3 text-white">
            <div className="w-10 h-10 rounded-lg bg-white/15 backdrop-blur-sm flex items-center justify-center shrink-0">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="font-semibold text-sm md:text-base">
                Protege tu identidad digital con <span className="font-heading font-bold text-yellow-300">VPN360</span>
              </p>
              <p className="text-xs text-white/85">
                VPN Móvil · eSIM Internacional · Cloud Storage · Suite completa de privacidad
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-white bg-white/15 hover:bg-white/25 backdrop-blur-sm px-4 py-2 rounded-full font-semibold text-sm transition-colors">
            Explorar planes
            <ArrowRight className="w-4 h-4" />
          </div>
        </div>
      </a>
    );
  }

  // LARGE banner (landing footer)
  return (
    <section
      className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-indigo-900 to-purple-900 text-white py-16 md:py-24"
      data-testid="vpn360-large-ad"
    >
      {/* Grid pattern overlay */}
      <div
        className="absolute inset-0 opacity-[0.08] pointer-events-none"
        style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,.6) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.6) 1px,transparent 1px)',
          backgroundSize: '40px 40px',
        }}
      />
      {/* Glow orb */}
      <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-blue-500/30 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full bg-purple-500/30 blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Copy */}
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full text-xs font-semibold text-yellow-300 mb-5">
              <Shield className="w-3.5 h-3.5" /> Servicio recomendado por IAASP
            </div>
            <h2 className="text-3xl md:text-5xl font-heading font-bold mb-4 leading-tight">
              Tu seguridad digital,<br /><span className="text-yellow-300">a un solo click</span>
            </h2>
            <p className="text-base md:text-lg text-white/85 mb-8 leading-relaxed max-w-xl">
              <strong className="text-white">VPN360</strong> reúne todos los servicios que un profesional de seguridad necesita:
              VPN cifrada de alta velocidad, eSIM internacional para viajes seguros
              y almacenamiento en la nube con cifrado militar.
            </p>

            <ul className="grid grid-cols-2 gap-3 mb-8 text-sm">
              {[
                'Sin registro de actividad',
                'Kill switch automático',
                '190+ países cubiertos',
                'Streaming sin bloqueos',
                'Cifrado AES-256',
                'Multi-dispositivo',
              ].map(feature => (
                <li key={feature} className="flex items-center gap-2 text-white/90">
                  <div className="w-5 h-5 rounded-full bg-green-500/20 border border-green-400/50 flex items-center justify-center shrink-0">
                    <Check className="w-3 h-3 text-green-400" />
                  </div>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>

            <div className="flex items-center gap-3 flex-wrap">
              <a
                href={VPN360_URL}
                target="_blank"
                rel="noopener noreferrer sponsored"
                className="inline-flex items-center gap-2 bg-yellow-400 hover:bg-yellow-300 text-slate-900 font-heading font-bold px-8 py-4 rounded-xl shadow-lg shadow-yellow-500/30 hover:shadow-yellow-400/50 transition-all hover:-translate-y-0.5"
                data-testid="vpn360-large-cta"
              >
                Ver planes en vpn360.app
                <ArrowRight className="w-5 h-5" />
              </a>
              <a
                href={VPN360_URL}
                target="_blank"
                rel="noopener noreferrer sponsored"
                className="text-sm text-white/70 hover:text-white underline underline-offset-4"
              >
                Conoce más
              </a>
            </div>
          </div>

          {/* Services cards */}
          <div className="grid grid-cols-1 gap-3">
            {services.map(({ icon: Icon, title, desc }, i) => (
              <a
                key={title}
                href={VPN360_URL}
                target="_blank"
                rel="noopener noreferrer sponsored"
                className="group flex items-center gap-4 bg-white/8 hover:bg-white/12 backdrop-blur-sm border border-white/15 hover:border-yellow-300/40 rounded-2xl p-5 transition-all hover:-translate-y-0.5"
                data-testid={`vpn360-service-${i}`}
              >
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-yellow-400 to-orange-400 flex items-center justify-center shrink-0 shadow-lg shadow-orange-500/30">
                  <Icon className="w-7 h-7 text-slate-900" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-heading font-bold text-lg text-white group-hover:text-yellow-300 transition-colors">{title}</h3>
                  <p className="text-sm text-white/70">{desc}</p>
                </div>
                <ArrowRight className="w-5 h-5 text-white/40 group-hover:text-yellow-300 group-hover:translate-x-1 transition-all shrink-0" />
              </a>
            ))}
          </div>
        </div>

        {/* Disclosure */}
        <p className="mt-10 text-center text-xs text-white/50">
          Anuncio · vpn360.app es un servicio independiente. IAASP Academy no gestiona ni administra la operación técnica del servicio.
        </p>
      </div>
    </section>
  );
};

export default VPN360Ad;
